import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip,
  Typography
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import Swal from "sweetalert2";
import "../../../../../assets/css/deals.css";
import CustomDialog from "../ui/HaklaiCustomDialog";
import DatePickerField from "../ui/HaklaiDatePickerField";
import LabeledTextField from "../ui/HaklaiLabeledTextField";
import { COMPACT_CURRENCY_LABELS } from "../haklaiConstants";
import {
  DEFAULT_HAKLAI_RESIDENTIAL_DEPRECIATION_PERCENT,
  HAKLAI_RESIDENTIAL_DEPRECIATION_RATE_OPTIONS,
  HAKLAI_RESIDENTIAL_DEP_TYPE_OPTIONS,
  createHaklaiResidentialDepreciationRange,
  getHaklaiDepreciationFieldErrors,
  getHaklaiPartDepreciationMode,
  getHaklaiResidentialDepreciationBuiltPercent,
  getHaklaiResidentialDepreciationPercent,
  HAKLAI_PROPERTY_DEPRECIATION_MODE,
  hasHaklaiDepreciationProfileInput,
  hasHaklaiDepreciationFieldErrors,
  normalizeHaklaiDepreciationState
} from "../haklaiDepreciationHelpers";
import {
  getHaklaiDepreciationDialogTitle,
  HAKLAI_DEPRECIATION_ACTIONS,
  HAKLAI_DEPRECIATION_DIALOG,
  HAKLAI_DEPRECIATION_ERRORS,
  HAKLAI_DEPRECIATION_LABELS,
  HAKLAI_DEPRECIATION_TOOLTIPS
} from "../haklaiDepreciationTexts";

const CUSTOM_BUILDING_PERCENT_VALUE = -1;
const RESIDENTIAL_BUILDING_PERCENT_OPTIONS = [
  { label: "100%", value: 1 },
  { label: "4/5", value: 0.8 },
  { label: "3/4", value: 0.75 },
  { label: "2/3", value: parseFloat((2 / 3).toFixed(3)) },
  { label: "1/2", value: 0.5 },
  { label: "1/3", value: parseFloat((1 / 3).toFixed(3)) },
  { label: "1/4", value: 0.25 },
  { label: "1/5", value: 0.2 },
  { label: HAKLAI_DEPRECIATION_LABELS.customBuildingPercent, value: CUSTOM_BUILDING_PERCENT_VALUE }
];

const getBuildingPercentState = (buildingPercent) => {
  const normalizedPercent = Number(buildingPercent);
  const matchingOption = RESIDENTIAL_BUILDING_PERCENT_OPTIONS.find((option) => (
    option.value !== CUSTOM_BUILDING_PERCENT_VALUE && option.value === normalizedPercent
  ));
  if (matchingOption) {
    return {
      depreciationBuiltPercentFromList: matchingOption.value,
      depreciationBuiltPercentNumerator: null,
      depreciationBuiltPercentDenominator: null
    };
  }

  return {
    depreciationBuiltPercentFromList: CUSTOM_BUILDING_PERCENT_VALUE,
    depreciationBuiltPercentNumerator: Number.isFinite(normalizedPercent)
      ? parseFloat((normalizedPercent * 100).toFixed(3))
      : null,
    depreciationBuiltPercentDenominator: 100
  };
};

const getBuildingPercentFromState = (state = {}) => {
  if (state.depreciationBuiltPercentFromList !== CUSTOM_BUILDING_PERCENT_VALUE) {
    return Number(state.depreciationBuiltPercentFromList);
  }
  const numerator = Number(state.depreciationBuiltPercentNumerator);
  const denominator = Number(state.depreciationBuiltPercentDenominator);
  if (!(numerator > 0) || !(denominator > 0)) return null;
  return parseFloat((numerator / denominator).toFixed(3));
};

const buildInitialState = ({ depreciation = {}, isResidentialAuto = false, part = {} }) => {
  const normalizedDepreciation = normalizeHaklaiDepreciationState(depreciation);
  const hasManualDepreciationValue = (
    normalizedDepreciation.depreciationResult !== null
    && normalizedDepreciation.depreciationResult !== undefined
    && normalizedDepreciation.depreciationResult !== ""
  );
  const normalizedDateRanges = Array.isArray(normalizedDepreciation.dateRanges) && normalizedDepreciation.dateRanges.length > 0
    ? normalizedDepreciation.dateRanges.map((range) => (
      isResidentialAuto
        ? createHaklaiResidentialDepreciationRange(range)
        : { ...range }
    ))
    : [isResidentialAuto ? createHaklaiResidentialDepreciationRange() : { startDate: "", endDate: "" }];
  const residentialBuildingPercent = getHaklaiResidentialDepreciationBuiltPercent({
    part,
    depreciation: normalizedDepreciation
  });

  return {
    dateRanges: isResidentialAuto ? normalizedDateRanges : [],
    depType: normalizedDepreciation.depType || "ptor",
    depreciationPercent: getHaklaiResidentialDepreciationPercent({
      depreciation: normalizedDepreciation
    }),
    ...getBuildingPercentState(residentialBuildingPercent),
    depreciationResult: hasManualDepreciationValue && Number.isFinite(Number(normalizedDepreciation.depreciationResult))
      ? Number(normalizedDepreciation.depreciationResult)
      : null
  };
};

const PERIOD_DATE_INPUT_WIDTH = 130;
const DIALOG_PAPER_CLASS_NAME = "deal-calc-dialog deal-depreciation-dialog";
const RESIDENTIAL_DEPRECIATION_RATE_LABEL_ID = "haklai-residential-depreciation-rate-label";

const renderChoicePills = ({ options, name, value, isNumeric, labelledBy, error, onChange }) => (
  <FormControl component="fieldset" error={error}>
    <RadioGroup
      row
      name={name}
      aria-labelledby={labelledBy}
      value={String(value)}
      onChange={(event) => {
        const nextValue = isNumeric ? Number(event.target.value) : event.target.value;
        onChange(nextValue);
      }}
    >
      {options.map((option) => (
        <FormControlLabel
          key={String(option.value)}
          value={String(option.value)}
          control={<Radio color="primary" />}
          label={option.label}
        />
      ))}
    </RadioGroup>
  </FormControl>
);

const renderDialogFooter = ({ onClear, onSave }) => (
  <div className="deal-depreciation-dialog-footer">
    <Button className="deal-depreciation-dialog-clear" variant="contained" onClick={onClear}>
      {HAKLAI_DEPRECIATION_ACTIONS.deleteData}
    </Button>
    <Button className="deal-depreciation-dialog-save" variant="contained" onClick={onSave}>
      {HAKLAI_DEPRECIATION_ACTIONS.save}
    </Button>
  </div>
);

const attachDialogDateRangeIds = (dateRanges = [], nextIdRef = { current: 0 }) => (
  (Array.isArray(dateRanges) ? dateRanges : []).map((range) => ({
    ...range,
    _localId: range?._localId || `haklai-dep-range-${nextIdRef.current += 1}`
  }))
);

const getFieldErrorsForState = ({
  mode,
  dateRanges = [],
  depType = "",
  depreciationBuiltPercent = null,
  depreciationResult = null,
  ownershipStartDate,
  soldPropertyDate
}) => getHaklaiDepreciationFieldErrors({
  mode,
  dateRanges,
  depType,
  depreciationBuiltPercent,
  depreciationResult,
  lowerBoundDate: ownershipStartDate,
  upperBoundDate: soldPropertyDate
});

const HaklaiPropertyDepreciationDialog = ({
  open,
  onClose,
  part,
  // Pre-resolved auto-numbered label (e.g. "מחסן (2)"); falls back to the
  // part's bare label when the parent hasn't computed one.
  displayLabel = "",
  depreciation,
  ownershipStartDate,
  soldPropertyDate,
  acquisitionCurrency,
  onSave,
  modeOverride = ""
}) => {
  const mode = useMemo(() => modeOverride || getHaklaiPartDepreciationMode(part), [modeOverride, part]);
  const isResidentialAuto = mode === HAKLAI_PROPERTY_DEPRECIATION_MODE.RESIDENTIAL_AUTO;
  const dialogWidth = isResidentialAuto ? 720 : 520;
  const nextDateRangeIdRef = useRef(0);
  const [state, setState] = useState(() => {
    const initialState = buildInitialState({ depreciation, isResidentialAuto, part });
    return {
      ...initialState,
      dateRanges: attachDialogDateRangeIds(initialState.dateRanges, nextDateRangeIdRef)
    };
  });
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    const nextState = buildInitialState({ depreciation, isResidentialAuto, part });
    const nextStateWithIds = {
      ...nextState,
      dateRanges: attachDialogDateRangeIds(nextState.dateRanges, nextDateRangeIdRef)
    };
    const nextFieldErrors = getFieldErrorsForState({
      mode,
      dateRanges: nextStateWithIds.dateRanges,
      depType: nextStateWithIds.depType,
      depreciationBuiltPercent: getBuildingPercentFromState(nextStateWithIds),
      depreciationResult: nextStateWithIds.depreciationResult,
      ownershipStartDate,
      soldPropertyDate
    });

    setState(nextStateWithIds);
    setShowErrors(
      open
      && hasHaklaiDepreciationProfileInput(depreciation)
      && hasHaklaiDepreciationFieldErrors(nextFieldErrors)
    );
  }, [
    depreciation,
    isResidentialAuto,
    mode,
    open,
    ownershipStartDate,
    part,
    soldPropertyDate
  ]);

  const depreciationBuiltPercent = useMemo(() => (
    getBuildingPercentFromState(state)
  ), [state]);

  const fieldErrors = useMemo(() => {
    return getFieldErrorsForState({
      mode,
      dateRanges: state.dateRanges,
      depType: state.depType,
      depreciationBuiltPercent,
      depreciationResult: state.depreciationResult,
      ownershipStartDate,
      soldPropertyDate
    });
  }, [
    mode,
    ownershipStartDate,
    soldPropertyDate,
    state.dateRanges,
    depreciationBuiltPercent,
    state.depType,
    state.depreciationResult
  ]);

  const handleFieldChange = (fieldName, value) => {
    setState((prev) => {
      if (fieldName !== "depreciationBuiltPercentFromList") {
        return { ...prev, [fieldName]: value };
      }

      if (Number(value) !== CUSTOM_BUILDING_PERCENT_VALUE) {
        return {
          ...prev,
          depreciationBuiltPercentFromList: Number(value),
          depreciationBuiltPercentNumerator: null,
          depreciationBuiltPercentDenominator: null
        };
      }

      const currentPercent = getBuildingPercentFromState(prev) || 1;
      return {
        ...prev,
        depreciationBuiltPercentFromList: CUSTOM_BUILDING_PERCENT_VALUE,
        depreciationBuiltPercentNumerator: parseFloat((currentPercent * 100).toFixed(3)),
        depreciationBuiltPercentDenominator: 100
      };
    });
  };

  const handleDateRangeChange = (index, fieldName, value) => {
    setState((prev) => {
      const nextDateRanges = [...prev.dateRanges];
      nextDateRanges[index] = {
        ...nextDateRanges[index],
        [fieldName]: value
      };
      return {
        ...prev,
        dateRanges: nextDateRanges
      };
    });
  };

  const addDateRange = () => {
    setState((prev) => ({
      ...prev,
      dateRanges: [
        ...prev.dateRanges,
        ...attachDialogDateRangeIds([
          isResidentialAuto ? createHaklaiResidentialDepreciationRange() : { startDate: "", endDate: "" }
        ], nextDateRangeIdRef)
      ]
    }));
  };

  const removeDateRange = (index) => {
    setState((prev) => {
      const nextDateRanges = prev.dateRanges.filter((range, rangeIndex) => rangeIndex !== index);
      return {
        ...prev,
        dateRanges: nextDateRanges.length
          ? nextDateRanges
          : attachDialogDateRangeIds([
            isResidentialAuto ? createHaklaiResidentialDepreciationRange() : { startDate: "", endDate: "" }
          ], nextDateRangeIdRef)
      };
    });
  };

  const handleSave = () => {
    if (hasHaklaiDepreciationFieldErrors(fieldErrors)) {
      setShowErrors(true);
      return;
    }

    if (isResidentialAuto) {
      onSave?.({
        depCurrency: acquisitionCurrency || "newShekel",
        depType: state.depType,
        depreciationPercent: Number(state.depreciationPercent) || DEFAULT_HAKLAI_RESIDENTIAL_DEPRECIATION_PERCENT,
        depreciationBuiltPercent,
        dateRanges: state.dateRanges.map((range) => ({
          startDate: range.startDate,
          endDate: range.endDate
        }))
      });
      onClose?.();
      return;
    }

    onSave?.({
      depType: "regTax",
      depCurrency: acquisitionCurrency || "newShekel",
      depreciationResult: Number(state.depreciationResult)
    });
    onClose?.();
  };

  const handleClearAll = () => {
    Swal.fire({
      title: HAKLAI_DEPRECIATION_DIALOG.clearTitle,
      text: HAKLAI_DEPRECIATION_DIALOG.clearText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: HAKLAI_DEPRECIATION_ACTIONS.delete,
      cancelButtonText: HAKLAI_DEPRECIATION_ACTIONS.cancel,
      reverseButtons: true
    }).then((result) => {
      if (!result.isConfirmed) return;
      onSave?.({});
      onClose?.();
    });
  };

  const resolvedTitleLabel = displayLabel || part?.label || "";
  const title = getHaklaiDepreciationDialogTitle(resolvedTitleLabel);

  return (
    <CustomDialog
      label={title}
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth={false}
      paperClassName={DIALOG_PAPER_CLASS_NAME}
      paperStyle={{
        width: dialogWidth,
        maxWidth: "calc(100vw - 64px)"
      }}
      isContentDivided={false}
      titleAlign="center"
    >
      <div className="deal-depreciation-dialog-body">
        {isResidentialAuto ? (
          <>
            <div className="deal-depreciation-dialog-section">
              <div className="deal-depreciation-dialog-row">
                <Typography className="deal-depreciation-dialog-label">
                  {HAKLAI_DEPRECIATION_LABELS.rentalIncomeTaxTrackTitle}
                </Typography>
                {renderChoicePills({
                  options: HAKLAI_RESIDENTIAL_DEP_TYPE_OPTIONS,
                  name: "depType",
                  value: state.depType,
                  error: showErrors && Boolean(fieldErrors.depType),
                  onChange: (nextValue) => handleFieldChange("depType", nextValue)
                })}
              </div>
              {showErrors && fieldErrors.depType ? (
                <Typography className="deal-depreciation-dialog-notice" variant="caption">
                  {fieldErrors.depType}
                </Typography>
              ) : null}
            </div>

            <div className="deal-depreciation-dialog-section">
              <div className="deal-depreciation-dialog-row">
                <div className="deal-depreciation-dialog-field-group">
                  <LabeledTextField
                    type="select"
                    value={state.depreciationBuiltPercentFromList}
                    labelWidth={135}
                    textFieldWidth={130}
                    name="depreciationBuiltPercentFromList"
                    label={HAKLAI_DEPRECIATION_LABELS.buildingPercent}
                    options={RESIDENTIAL_BUILDING_PERCENT_OPTIONS}
                    error={showErrors && Boolean(fieldErrors.depreciationBuiltPercent)}
                    helperText={
                      showErrors
                      && state.depreciationBuiltPercentFromList !== CUSTOM_BUILDING_PERCENT_VALUE
                        ? fieldErrors.depreciationBuiltPercent
                        : ""
                    }
                    onChange={(event) => handleFieldChange("depreciationBuiltPercentFromList", event.target.value)}
                  />
                  {state.depreciationBuiltPercentFromList === CUSTOM_BUILDING_PERCENT_VALUE ? (
                    <LabeledTextField
                      type="number"
                      value={state.depreciationBuiltPercentNumerator ?? ""}
                      labelWidth={95}
                      textFieldWidth={95}
                      min={0}
                      max={100}
                      name="depreciationBuiltPercentNumerator"
                      label={HAKLAI_DEPRECIATION_LABELS.customPercent}
                      currency="%"
                      error={showErrors && Boolean(fieldErrors.depreciationBuiltPercent)}
                      helperText={showErrors ? fieldErrors.depreciationBuiltPercent : ""}
                      onChange={(event) => {
                        handleFieldChange("depreciationBuiltPercentNumerator", event.target.value);
                        handleFieldChange("depreciationBuiltPercentDenominator", 100);
                      }}
                    />
                  ) : null}
                </div>
                <div className="deal-depreciation-dialog-field-group">
                  <Typography
                    id={RESIDENTIAL_DEPRECIATION_RATE_LABEL_ID}
                    className="deal-depreciation-dialog-label"
                  >
                    {HAKLAI_DEPRECIATION_LABELS.depreciationRate}
                  </Typography>
                  {renderChoicePills({
                    options: HAKLAI_RESIDENTIAL_DEPRECIATION_RATE_OPTIONS,
                    name: "depreciationPercent",
                    value: state.depreciationPercent || DEFAULT_HAKLAI_RESIDENTIAL_DEPRECIATION_PERCENT,
                    isNumeric: true,
                    labelledBy: RESIDENTIAL_DEPRECIATION_RATE_LABEL_ID,
                    onChange: (nextValue) => handleFieldChange(
                      "depreciationPercent",
                      nextValue || DEFAULT_HAKLAI_RESIDENTIAL_DEPRECIATION_PERCENT
                    )
                  })}
                </div>
              </div>
            </div>

            <div className="deal-depreciation-dialog-section">
              {state.dateRanges.map((range, index) => (
                <div className="deal-depreciation-dialog-row is-dates" key={range._localId}>
                  <div className="deal-depreciation-dialog-date-fields">
                    <Typography className="deal-depreciation-dialog-label">
                      {HAKLAI_DEPRECIATION_LABELS.rentedFromDate}
                    </Typography>
                    <DatePickerField
                      name={`residentialStartDate_${index}`}
                      textFieldWidth={PERIOD_DATE_INPUT_WIDTH}
                      value={range.startDate}
                      invalidInputMessage={HAKLAI_DEPRECIATION_ERRORS.startDateInvalid}
                      validateInvalidInputOnBlur
                      errorDisplay="tooltip"
                      minDate={ownershipStartDate}
                      maxDate={soldPropertyDate}
                      error={showErrors && Boolean(fieldErrors.dateRanges[index]?.startDate)}
                      helperText={showErrors ? (fieldErrors.dateRanges[index]?.startDate || "") : ""}
                      onChange={(event) => handleDateRangeChange(index, "startDate", event.target.value)}
                    />
                    <Typography className="deal-depreciation-dialog-label">
                      {HAKLAI_DEPRECIATION_LABELS.untilDate}
                    </Typography>
                    <DatePickerField
                      name={`residentialEndDate_${index}`}
                      textFieldWidth={PERIOD_DATE_INPUT_WIDTH}
                      value={range.endDate}
                      invalidInputMessage={HAKLAI_DEPRECIATION_ERRORS.endDateInvalid}
                      validateInvalidInputOnBlur
                      errorDisplay="tooltip"
                      minDate={ownershipStartDate}
                      maxDate={soldPropertyDate}
                      error={showErrors && Boolean(fieldErrors.dateRanges[index]?.endDate)}
                      helperText={showErrors ? (fieldErrors.dateRanges[index]?.endDate || "") : ""}
                      onChange={(event) => handleDateRangeChange(index, "endDate", event.target.value)}
                    />
                  </div>
                  <div className="deal-depreciation-dialog-date-actions">
                    <Tooltip title={HAKLAI_DEPRECIATION_TOOLTIPS.addRange}>
                      <IconButton
                        className="deal-depreciation-dialog-add"
                        size="small"
                        aria-label={HAKLAI_DEPRECIATION_TOOLTIPS.addRange}
                        onClick={addDateRange}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={HAKLAI_DEPRECIATION_TOOLTIPS.deleteRange}>
                      <IconButton
                        className="deal-depreciation-dialog-delete"
                        size="small"
                        aria-label={HAKLAI_DEPRECIATION_TOOLTIPS.deleteRange}
                        onClick={() => removeDateRange(index)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
            {renderDialogFooter({ onClear: handleClearAll, onSave: handleSave })}
          </>
        ) : (
          <>
            <div className="deal-depreciation-dialog-section">
              <Typography className="deal-depreciation-dialog-notice">
                {HAKLAI_DEPRECIATION_DIALOG.manualForm11Notice}
              </Typography>
              <div className="deal-depreciation-dialog-row">
                <LabeledTextField
                  type="number"
                  value={state.depreciationResult ?? ""}
                  labelWidth={170}
                  textFieldWidth={170}
                  min={0}
                  name="depreciationResult"
                  currency={COMPACT_CURRENCY_LABELS[acquisitionCurrency] || COMPACT_CURRENCY_LABELS.newShekel}
                  label={HAKLAI_DEPRECIATION_LABELS.depreciationAmount}
                  error={showErrors && Boolean(fieldErrors.depreciationResult)}
                  helperText={showErrors ? (fieldErrors.depreciationResult || "") : ""}
                  onChange={(event) => handleFieldChange("depreciationResult", event.target.value)}
                />
              </div>
            </div>
            {renderDialogFooter({ onClear: handleClearAll, onSave: handleSave })}
          </>
        )}
      </div>
    </CustomDialog>
  );
};

export default HaklaiPropertyDepreciationDialog;
